/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useRef} from 'react';
import {makeStyles} from '@material-ui/core';
import {DragIcon} from '@ion/dls/src/icons/DragIcon';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';

type RowWithId = {rowId: string};

export interface EditableRowsGridActionsCellProps {
  data: RowWithId;
  onDeleteRow: (rowId: string) => void;
  className: string;
  dragHandleClass: string;
  registerRowDragger?: (rowDraggerElement: HTMLElement, dragStartPixels?: number) => void;
}

const useStyles = makeStyles(() => ({
  actionsCell : {
    display        : 'flex',
    justifyContent : 'flex-end',
    gap            : '10px',
    width          : '100%',
    padding        : '0 10px 0 4px'
  }
}));

/**
 * Renders the editable-grid actions cell with drag handle and row delete controls.
 *
 * ```typescript
 * <EditableRowsGridActionsCell
 *   data={data}
 *   onDeleteRow={onDeleteRow}
 *   className={className}
 *   dragHandleClass={dragHandleClass}
 *   registerRowDragger={registerRowDragger}
 * />
 * ```
 *
 * @param   data              Row data containing the rowId used for delete actions.
 * @param   onDeleteRow       Invoked when delete is triggered for the current row.
 * @param   className         Class name for the delete icon container.
 * @param   dragHandleClass   Class name for the drag handle container.
 * @param   registerRowDragger Optional AG Grid row-drag registration callback.
 * @returns The editable rows grid actions cell component.
 */
const EditableRowsGridActionsCell = ({
  data,
  onDeleteRow,
  className,
  dragHandleClass,
  registerRowDragger
}: EditableRowsGridActionsCellProps) => {
  const classes = useStyles();
  const dragRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (dragRef.current && registerRowDragger) {
      registerRowDragger(dragRef.current, 4);
    }
  }, [registerRowDragger]);

  const onDeleteClick = useCallback(() => {
    if (data?.rowId) {
      onDeleteRow(data.rowId);
    }
  }, [data, onDeleteRow]);

  const onDeleteKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (data?.rowId) {
        onDeleteRow(data.rowId);
      }
    }
  }, [data, onDeleteRow]);

  return (
    <div className={classes.actionsCell}>
      <div ref={dragRef}
        className={dragHandleClass}
        tabIndex={-1}>
        <DragIcon color={dlsColor('grey3')} />
      </div>
      <div className={className}
        tabIndex={-1}
        onClick={onDeleteClick}
        onKeyDown={onDeleteKeyDown}>
        <MaterialExitCloseIcon color={dlsColor('grey3')} />
      </div>
    </div>
  );
};

export default EditableRowsGridActionsCell;
